/**
 * Runtime, session-scoped override for prototype-mode visibility.
 *
 * The build-time flag (`isPrototypeEnabled`) decides whether prototype code
 * exists at all. This override lets someone browsing a QA/staging build quiet
 * the prototype chrome — or opt into full production parity — without a rebuild,
 * by setting a query param that is remembered for the browser-tab session:
 *
 *   ?prototype=no-chrome  → hide the border + label, keep prototype content
 *   ?prototype=off        → behave exactly like production (hide everything)
 *   ?prototype=on         → clear a previously stored override
 *
 * The override can only ever *reduce* visibility — it never turns prototype
 * mode on when the build flag is off.
 */
import { resolvePrototypeEnabled } from './prototypeFlag';

export type PrototypeOverride = 'default' | 'chrome-hidden' | 'disabled';

const STORAGE_KEY = 'seaters:prototype-override';
const QUERY_PARAM = 'prototype';

const QUERY_VALUE_TO_OVERRIDE: Record<string, PrototypeOverride> = {
  'no-chrome': 'chrome-hidden',
  'chrome-off': 'chrome-hidden',
  off: 'disabled',
  hidden: 'disabled',
  on: 'default',
  default: 'default',
  reset: 'default',
};

const isOverride = (value: string | null): value is PrototypeOverride =>
  value === 'default' || value === 'chrome-hidden' || value === 'disabled';

const listeners = new Set<() => void>();

const readStoredOverride = (): PrototypeOverride => {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return isOverride(stored) ? stored : 'default';
  } catch {
    // sessionStorage can throw in private mode / sandboxed frames.
    return 'default';
  }
};

const persistOverride = (value: PrototypeOverride) => {
  try {
    window.sessionStorage.setItem(STORAGE_KEY, value);
  } catch {
    // Best-effort: an unavailable sessionStorage just means no persistence.
  }
};

const stripQueryParam = () => {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(QUERY_PARAM)) {
      return;
    }
    url.searchParams.delete(QUERY_PARAM);
    window.history.replaceState(
      window.history.state,
      '',
      `${url.pathname}${url.search}${url.hash}`
    );
  } catch {
    // Leaving the param in the URL is harmless if this fails.
  }
};

const resolveInitialOverride = (): PrototypeOverride => {
  if (typeof window === 'undefined') {
    return 'default';
  }

  // The override is a QA/staging affordance. Where prototype mode isn't built
  // in (production), it can have no effect anyway, so we don't read the URL,
  // rewrite history, or touch sessionStorage at all.
  const buildFlagEnabled = resolvePrototypeEnabled(
    import.meta.env.SEATERS_PROTOTYPE_ENABLED,
    import.meta.env.MODE
  );
  if (!buildFlagEnabled) {
    return 'default';
  }

  let raw: string | null;
  try {
    raw = new URL(window.location.href).searchParams.get(QUERY_PARAM);
  } catch {
    raw = null;
  }

  if (raw != null) {
    const mapped = QUERY_VALUE_TO_OVERRIDE[raw.trim().toLowerCase()];
    // Only a recognized value updates state; a typo must not silently clobber
    // an override already stored for this tab session.
    if (mapped) {
      persistOverride(mapped);
      stripQueryParam();
      return mapped;
    }
  }

  return readStoredOverride();
};

let currentOverride: PrototypeOverride = resolveInitialOverride();

export function getPrototypeOverride(): PrototypeOverride {
  return currentOverride;
}

export function setPrototypeOverride(value: PrototypeOverride): void {
  currentOverride = value;
  persistOverride(value);
  listeners.forEach((listener) => listener());
}

export function subscribePrototypeOverride(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}
